'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { partition } from 'lodash';
import { usePathname } from 'next/navigation';
import type { FetchResult } from '@apollo/client';
import type { ApolloClient } from '@frameio/graphql-client';
import { useApolloClient } from '@frameio/graphql-client';
import { ProjectName, TransferInfo } from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import type {
  DownloadAssetArgs,
  DownloadAssetsReturn,
  TranscodeKeys,
} from '@frameio/shared/assets/types';
import {
  useActiveAccountId,
  useActiveProjectId,
  useActiveShareId,
} from '@frameio/shared/contexts';
import { useAccountVersion } from '@frameio/shared/contexts/ActiveAccountIdContext/useAccountVersion';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import { useOpenInBrowserMutation } from '@frameio/shared/integrations/gql';
import { useIsMountableStorageEnabled } from '@frameio/shared/plans/hooks/useIsMountableStorageEnabled';
import { getPageType } from '@frameio/shared/routing';
import { useTrack, useTrackUnsafe } from '@frameio/shared/segment/hooks';
import {
  downloadUrlsAsFiles,
  openDownloadUrlsInBrowser,
} from '@frameio/shared/utils/downloadUrlsAsFiles';
import { shareAuth } from '@frameio/utils';
import { isMacOs, isWindows } from '@frameio/utils/browser';
import { formatFileSizeInMB } from '@frameio/utils/formatters';
import { ToastAlert, useToast } from '@frameio/vapor';
import { assetsStorage } from '../../shared';
import { AppLaunchModal, DownloadModal, WarningModal } from '../components';
import {
  DRIVE_PROTOCOL_URL_PREFIX,
  TRANSFER_PROTOCOL_URL_PREFIX,
} from '../constants';
import {
  type MarkSharedAssetsDownloadedMutation,
  type MarkSharedAssetsDownloadedMutationVariables,
  useMarkSharedAssetsDownloadedMutation,
} from '../gql/generated/MarkSharedAssetsDownloaded';
import {
  type AssetsForDownload,
  type DownloadableAsset,
  fetchAssetsForDownload,
  fetchFolderChildrenForDownload,
} from './fetchAssetsForDownload';
import TEXT from './generated/TEXT/useDownloadAssets';
import { getTranscodeByKey } from './getTranscodeByKey';
import { useImportAssetToPremiere } from './useImportAssetsToPremiere';

type DownloadAssetsOpts = {
  downloadAllChildren?: boolean;
};

type DownloadTrackingFn = (
  assets: any[] | null | undefined,
  transcodeKey: string | undefined
) => void;

type MarkSharedAssetsDownloadedFn = (options?: {
  variables: MarkSharedAssetsDownloadedMutationVariables;
}) => Promise<FetchResult<MarkSharedAssetsDownloadedMutation>>;

const DEFAULT_OPTS: DownloadAssetsOpts = {
  downloadAllChildren: true,
};

const DEFAULT_ARGS: DownloadAssetArgs = {
  transcodeKey: 'original',
  force: false,
  trackDownloadingState: true,
};

const PAGE_TYPE_TO_SOURCE_MAP: Record<string, string> = {
  collection: 'Collection',
  folder: 'Project',
  folderViewer: 'Viewer',
  allShares: 'Share',
  shareReviewFinder: 'Share',
  shareReview: 'Share',
  shareReviewReel: 'Share',
};

const DOWNLOAD_WARNING_ASSET_LIMIT = 5;
const DOWNLOAD_WARNING_ASSET_SIZE = 1024 * 1024 * 1024 * 2;
const PAGE_SIZE = 50;

const TOAST_OPTIONS = { duration: 4000 };
const STILL_DOWNLOADING_TIMER = 150;

TEXT.DEFINE(
  {
    errorMessage: (count: number) =>
      `Couldn't download ${plural(count, {
        other: `${count} items`,
        one: `${count} item`,
      })}`,
    warningMessage: 'Some files could not be downloaded.',
    fetchDownloadAssetFailedWarning: 'Failed to fetch download asset',
    toastTitleDrive: 'Downloading in Drive app',
    toastTitleTransfer: 'Downloading in Transfer app',
    toastTitleBrowser: 'Downloading in browser',
  },
  { plural }
);

function haveMinCount(assets: AssetsForDownload) {
  if (!assets) {
    return false;
  }
  return assets.length > DOWNLOAD_WARNING_ASSET_LIMIT;
}

function hasFolder(assets: AssetsForDownload) {
  if (!assets) {
    return false;
  }
  return assets.some((asset) => asset?.__typename === 'FolderAsset');
}

// Transfer/Drive protocol URLs carry no transcode key, so the apps always fetch
// originals — any other rendition has to download in the browser.
function canUseDesktopApp(transcodeKey: TranscodeKeys | null | undefined) {
  return !transcodeKey || transcodeKey === 'original';
}

function haveMinTotalFileSize(
  assets: AssetsForDownload,
  transcodeKey: TranscodeKeys | undefined,
  desktopAppIsAvailable: boolean
) {
  if (!desktopAppIsAvailable) {
    return false;
  }
  return (
    getDownloadFileSize(assets, (transcodeKey || 'original') as TranscodeKeys) >
    DOWNLOAD_WARNING_ASSET_SIZE
  );
}

// Generates the "passphrase" part of the protocol URL
// that the Transfer app uses.
function getPassphraseParam(passphrase?: string): string {
  if (!passphrase) return '';
  return `&password=${encodeURIComponent(passphrase)}`;
}

// Ensures that certain invalid pathnames will be correctly handled as
// "not found" instead of causing a 500 error and showing the error boundary.
// @see WEB-1854
function getDownloadSource(pathname: string): string | undefined {
  try {
    const pageType = getPageType(pathname);
    return pageType
      ? PAGE_TYPE_TO_SOURCE_MAP[pageType] || undefined
      : undefined;
  } catch (e) {
    return undefined;
  }
}

/**
 * Different dialogs/popups the UI can show
 *
 * - 'None' - No dialog is shown
 * - 'Warning' - Download warning dialog is shown - when the Transfer App is NOT available, or a proxy rendition was requested
 * - 'Transfer' - Option to download in Transfer app shown - when when the Transfer App IS available
 * - 'DownloadDrive' - Option to retry download in Drive app, or download Drive app - follows "Transfer" state
 * - 'DownloadTransfer' - Option to retry download in Transfer app, or download Transfer app - follows "Transfer" state
 */
type DialogState =
  | 'None'
  | 'Warning'
  | 'Transfer'
  | 'DownloadDrive'
  | 'DownloadTransfer';

/**
 * The useDownloadAssets hook is used to download all assets from an array of asset
 * ids. Any folders included will have their children recursively downloaded as
 * well, but this can be turned off by setting the downloadAllChildren option to
 * false.
 *
 * The user will also be warned if they are trying to download more than 5 files,
 * or if the download contains a folder. An AlertDialog is displayed, and the
 * download only proceeds if the user hits "Continue." The AlertDialog
 * (donwloadWarningDialog) has to be rendered.
 *
 * @param opts.downloadAllChildren whether or not to traverse down all folders and
 *                                 download all of the children assets
 * @returns value.downloadAssets a function to call when ready to download the assets
 * @returns value.downloadWarningDialog an AlertDialog that needs to be rendered for
 *                                      warning the user about large downloads.
 *                                      this has to be rendered, or else large
 *                                      downloads will fail silently.
 * @returns value.isDownloading whether a download is currently happening
 */
export function useDownloadAssets(
  opts?: DownloadAssetsOpts
): DownloadAssetsReturn {
  const { downloadAllChildren } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const [isDownloading, setIsDownloading] = useState(false);
  const stillDownloading = useRef(false);
  const { addToast } = useToast();
  const client = useApolloClient();
  const dismissRef = useRef(null);
  const warningAssets = useRef<Array<{
    id: string;
    typename: string;
  }> | null>(null);
  const warningTranscodeKey = useRef<TranscodeKeys | undefined>(null);
  const pathname = usePathname();
  const downloadSource = getDownloadSource(pathname);
  const [desktopAppIsAvailable, setDesktopAppIsAvailable] = useState(false);
  const activeAccountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const shareId = useActiveShareId();
  const accountVersion = useAccountVersion();
  const passphrase = shareId ? shareAuth.getPassphraseById(shareId) : undefined;
  const userId = useGetActiveUserId();
  const track = useTrack();
  const trackUnsafe = useTrackUnsafe();
  const [transferModalSegmentJobId, setTransferModalSegmentJobId] =
    useState('');
  const [isShareDownloadAll, setIsShareDownloadAll] = useState<
    boolean | undefined
  >(false);
  // We need to make sure that only one "Downloading in X" toast is shown per operation
  const didShowDownloadToast = useRef(false);
  const [dialogState, setDialogState] = useState<DialogState>('None');

  const isPremiereClient = useIsMatchingClientName('premiere');
  const isAfterEffectsClient = useIsMatchingClientName('aftereffects');
  const { importAssetIds } = useImportAssetToPremiere();
  const [openInBrowser] = useOpenInBrowserMutation({
    context: { resolveWithUXP: true },
  });

  const deliverDownloadUrls = useCallback(
    (urls: string[]) => {
      if (isAfterEffectsClient) {
        openDownloadUrlsInBrowser(urls, openInBrowser);
        return;
      }
      downloadUrlsAsFiles(urls);
    },
    [isAfterEffectsClient, openInBrowser]
  );

  // Extract TEXT properties to avoid calling hooks inside useCallback
  const textWarningMessage = TEXT.warningMessage;
  const textErrorMessage = TEXT.errorMessage;

  const isMountableStorageEnabled = useIsMountableStorageEnabled();

  useEffect(() => {
    setDesktopAppIsAvailable(isMacOs() || isWindows());
  }, []);

  const displayWarning = useCallback(() => {
    if (
      isAfterEffectsClient ||
      !desktopAppIsAvailable ||
      !canUseDesktopApp(warningTranscodeKey.current)
    ) {
      setDialogState('Warning');
      return;
    }
    setDialogState('Transfer');
    // Segment: generate a new job ID and send "modal shown" event
    const segmentJobId = crypto.randomUUID();
    setTransferModalSegmentJobId(segmentJobId);
    track('download-with-transfer-modal-shown', {
      transfer_job_id: segmentJobId,
    });
    // Reset download toast tracker
    didShowDownloadToast.current = false;
  }, [desktopAppIsAvailable, isAfterEffectsClient, track]);

  /**
   * 1. Show Transfer/Drive/Browser download modal
   * 2. Show launch modal: Launches app + links to download app files + "try again" fallback to return to step 1
   */
  const renderDownloadModals = useCallback(
    ({
      openTransferApp,
      openDriveApp,
      downloadInBrowser,
    }: {
      openTransferApp: () => void;
      openDriveApp: () => void;
      downloadInBrowser: () => void;
    }) => {
      return (
        <>
          {/* Modal 1: Download in Transfer/Drive app vs. in browser */}
          <DownloadModal
            isShowing={dialogState === 'Transfer'}
            onCancel={() => {
              setIsDownloading(false);
              setDialogState('None');
              warningTranscodeKey.current = null;
              track('download-with-transfer-modal-closed');
            }}
            onDownloadInBrowser={downloadInBrowser}
            onDownloadInDriveApp={() => {
              openDriveApp();
              setDialogState('DownloadDrive');
            }}
            onDownloadInTransferApp={() => {
              openTransferApp();
              setDialogState('DownloadTransfer');
            }}
            segmentId={transferModalSegmentJobId}
            showDriveApp={isMountableStorageEnabled}
          />
          {/* Modal 2: Download Transfer/Drive app and/or retry download */}
          <AppLaunchModal
            isShowing={
              dialogState === 'DownloadDrive' ||
              dialogState === 'DownloadTransfer'
            }
            {...(dialogState === 'DownloadDrive'
              ? {
                  onRetry: openDriveApp,
                  variant: 'drive',
                }
              : {
                  onRetry: openTransferApp,
                  variant: 'transfer',
                })}
            onExit={() => {
              setIsDownloading(false);
              setDialogState('None');
              warningTranscodeKey.current = null;
            }}
          />
        </>
      );
    },
    [dialogState, transferModalSegmentJobId, isMountableStorageEnabled, track]
  );

  const showDownloadToast = useCallback(
    (title: string, callback: () => void) => {
      // Only show one toast at a time
      if (didShowDownloadToast.current) {
        return undefined;
      }
      didShowDownloadToast.current = true;
      callback();
      return addToast(
        (closeToast, toastCloseLabel) => (
          <ToastAlert
            closeLabel={toastCloseLabel}
            onRequestClose={closeToast}
            title={title}
            variant="success"
          />
        ),
        TOAST_OPTIONS
      );
    },
    [addToast]
  );

  /**
   * Fires the 'download-started' Segment event.
   * @param assets the array of asset data to be downloaded
   * @param transcodeKey the key to select the version of media being downloaded
   */
  const trackDownloadAssets = useCallback(
    (
      assets: any[] | null | undefined,
      transcodeKey: string | undefined
    ): void => {
      if (downloadSource && assets) {
        const totalFileSize = getDownloadFileSize(
          assets,
          (transcodeKey || 'original') as TranscodeKeys
        );
        if (totalFileSize > 0) {
          trackUnsafe('download-started', {
            download_source: downloadSource,
            item_count: assets?.length,
            total_file_size: formatFileSizeInMB(totalFileSize),
            asset_ids: assets.map((asset) => asset.id),
          });
        }
      }
    },
    [downloadSource, trackUnsafe]
  );

  /**
   * Fires the mutation to mark shared assets as downloaded
   */
  const [markSharedAssetsDownloaded] = useMarkSharedAssetsDownloadedMutation();

  /**
   * @param assetIds the id or array of ids for the asset(s) to download
   * @param force force the download, without showing the dialog
   * @param trackDownloadingState does the download need to be tracked? should
   *                              only be true if the isDownloading state will
   *                              be displayed to the user
   */
  const downloadAssetsCallback = useCallback(
    async (assetIds: string | string[], args?: DownloadAssetArgs) => {
      if (isPremiereClient) {
        importAssetIds(assetIds, args?.transcodeKey);
        return;
      }
      const { transcodeKey, force, trackDownloadingState, shareDownloadAll } = {
        ...DEFAULT_ARGS,
        ...args,
      };
      const ids = typeof assetIds === 'string' ? [assetIds] : assetIds;
      stillDownloading.current = true;
      setIsShareDownloadAll(shareDownloadAll);

      // only update the isDownloading state if the download is taking a while
      window.setTimeout(() => {
        if (trackDownloadingState && stillDownloading.current) {
          setIsDownloading(true);
        }
      }, STILL_DOWNLOADING_TIMER);

      try {
        /**
         * setting fetchPolicy to network-only since the download urls have to be
         * signed, which means we need to get the latest ones before attempting to
         * actually download files
         */
        const assets = await fetchAssetsForDownload(
          client,
          { accountId: activeAccountId, shareId },
          ids,
          /**
           * if force is true, then this query has already run once and we can read
           * the results from cache.
           */
          force ? 'cache-first' : 'network-only'
        );

        if (
          !force &&
          assets &&
          (haveMinCount(assets) ||
            hasFolder(assets) ||
            // The size check only exists to offer the desktop apps.
            haveMinTotalFileSize(
              assets,
              transcodeKey,
              desktopAppIsAvailable && canUseDesktopApp(transcodeKey)
            ))
        ) {
          if (!assets) {
            return;
          }
          warningAssets.current = assets.flatMap((asset) => {
            if (!asset) return [];
            return [{ id: asset.id, typename: asset.__typename! }];
          });
          warningTranscodeKey.current = transcodeKey;
          displayWarning();
          return;
        }

        // kick off the flow
        const successfullyDownloadedAllFiles = await downloadAssets(
          client,
          assets,
          transcodeKey || 'original',
          downloadAllChildren,
          trackDownloadAssets,
          shareId,
          markSharedAssetsDownloaded,
          deliverDownloadUrls
        );

        if (trackDownloadingState) {
          setIsDownloading(false);
          stillDownloading.current = false;
        }

        if (!successfullyDownloadedAllFiles) {
          addToast(
            (closeToast, toastCloseLabel) => (
              <ToastAlert
                closeLabel={toastCloseLabel}
                onRequestClose={closeToast}
                title={textWarningMessage}
                variant="warning"
              />
            ),
            TOAST_OPTIONS
          );
        }
      } catch (_) {
        addToast(
          (closeToast, toastCloseLabel) => (
            <ToastAlert
              closeLabel={toastCloseLabel}
              onRequestClose={closeToast}
              title={textErrorMessage(ids.length) as string}
              variant="error"
            />
          ),
          TOAST_OPTIONS
        );

        setIsDownloading(false);
        stillDownloading.current = false;
      }
    },
    [
      addToast,
      activeAccountId,
      client,
      desktopAppIsAvailable,
      displayWarning,
      downloadAllChildren,
      deliverDownloadUrls,
      importAssetIds,
      isPremiereClient,
      markSharedAssetsDownloaded,
      shareId,
      textErrorMessage,
      textWarningMessage,
      trackDownloadAssets,
    ]
  );

  const openProtocolURL = useCallback(
    (name: string, type: string, prefix: string) => {
      if (!warningAssets.current) {
        return;
      }
      const openURL = (url: string) => window.location.assign(url);

      // If we're using Download All from a Share, we don't include asset IDs, just the share ID
      if (isShareDownloadAll) {
        openURL(
          `${prefix}&shareId=${shareId}&name=${name}${getPassphraseParam(
            passphrase
          )}&downloadSource=${downloadSource}`
        );
      } else {
        const formattedAssets = `assetIds=${warningAssets.current
          .map((asset) => asset.id)
          .join()}`;
        if (shareId) {
          // If we're downloading specific assets from a Share, DO include asset IDs
          // and the share ID and passphrase, if needed
          openURL(
            `${prefix}${formattedAssets}&shareId=${shareId}&name=${name}&type=${type}${getPassphraseParam(
              passphrase
            )}&downloadSource=${downloadSource}`
          );
        } else {
          // Normal case: downloading from Finder
          openURL(
            `${prefix}${formattedAssets}&accountId=${activeAccountId}&userId=${userId}&name=${name}&type=${type}&accountVersion=${accountVersion}&downloadSource=${downloadSource}`
          );
        }
      }
      setIsDownloading(false);
    },
    [
      setIsDownloading,
      activeAccountId,
      userId,
      isShareDownloadAll,
      passphrase,
      shareId,
      accountVersion,
      downloadSource,
    ]
  );

  const downloadWarningDialog = useMemo(() => {
    function downloadInBrowser() {
      if (!warningAssets.current) return;

      downloadAssetsCallback(
        warningAssets.current.map((asset) => asset.id),
        {
          force: true,
          transcodeKey: warningTranscodeKey.current || 'original',
        }
      );
      setDialogState('None');
      warningTranscodeKey.current = null;
    }
    async function openApp(prefix: string) {
      if (!warningAssets.current) return;

      const assetToUse = client.readFragment({
        id: client.cache.identify({
          __typename: warningAssets.current[0].typename,
          id: warningAssets.current[0].id,
        }),
        fragment: TransferInfo,
        fragmentName: 'TransferInfo',
      });
      if (!assetToUse) {
        addToast(
          (closeToast, toastCloseLabel) => (
            <ToastAlert
              closeLabel={toastCloseLabel}
              onRequestClose={closeToast}
              title={TEXT.fetchDownloadAssetFailedWarning}
              variant="error"
            />
          ),
          TOAST_OPTIONS
        );
        setIsDownloading(false);
        return;
      }
      // Special case: downloading entire project
      if (!assetToUse.parent && !shareId) {
        const currentProject = client.readFragment({
          id: client.cache.identify({
            __typename: 'Project',
            id: projectId,
          }),
          fragment: ProjectName,
          fragmentName: 'ProjectName',
        });
        if (!currentProject) {
          return;
        }
        openProtocolURL(
          currentProject.name,
          'FolderAsset',
          `${prefix}download?`
        );
        return;
      }
      openProtocolURL(
        assetToUse.name,
        assetToUse.__typename || 'FileAsset',
        `${prefix}download?`
      );
    }
    function openTransferApp() {
      return openApp(TRANSFER_PROTOCOL_URL_PREFIX);
    }
    function openDriveApp() {
      return openApp(DRIVE_PROTOCOL_URL_PREFIX);
    }
    if (dialogState === 'None') {
      return undefined;
    }

    // Base case - feature flag for allowing Transfer app downloads is switched off
    if (
      !desktopAppIsAvailable ||
      isAfterEffectsClient ||
      dialogState === 'Warning'
    ) {
      return (
        <WarningModal
          dismissRef={dismissRef}
          onDismiss={() => {
            setIsDownloading(false);
            setDialogState('None');
          }}
          onDownload={() => {
            downloadInBrowser();
          }}
          onExit={() => {
            warningAssets.current = null;
            warningTranscodeKey.current = null;
          }}
          showWarning={dialogState === 'Warning'}
        />
      );
    }
    // Feature flag is on; no download preference has been set
    const downloadPreference = assetsStorage.get('downloadPreference');

    // We set a download preference after the first modal,
    // so make sure to show the second modal if we're on our first time
    // through this flow.
    if (desktopAppIsAvailable && !downloadPreference && !isAfterEffectsClient) {
      return renderDownloadModals({
        openTransferApp,
        openDriveApp,
        downloadInBrowser,
      });
    }

    // Make sure we're not showing extra downloads UI
    if (dialogState !== 'Transfer') {
      return undefined;
    }

    // If download has started
    if (downloadPreference === 'driveApp') {
      return showDownloadToast(TEXT.toastTitleDrive, openDriveApp);
    }
    if (downloadPreference === 'transferApp') {
      return showDownloadToast(TEXT.toastTitleTransfer, openTransferApp);
    }

    return showDownloadToast(TEXT.toastTitleBrowser, downloadInBrowser);
  }, [
    dialogState,
    desktopAppIsAvailable,
    isAfterEffectsClient,
    showDownloadToast,
    downloadAssetsCallback,
    client,
    shareId,
    openProtocolURL,
    addToast,
    projectId,
    renderDownloadModals,
  ]);

  return {
    downloadAssets: downloadAssetsCallback,
    downloadWarningDialog,
    isDownloading,
  };
}

// recursively fetches all the children of a folder and kicks off downloads
async function downloadFolder(
  client: ApolloClient<any>,
  folderId: string,
  transcodeKey: TranscodeKeys,
  downloadAllChildren = DEFAULT_OPTS.downloadAllChildren,
  afterOffset = 0,
  trackingFn: DownloadTrackingFn | undefined = undefined,
  shareId: string | undefined = undefined,
  markSharedAssetsDownloaded: MarkSharedAssetsDownloadedFn,
  deliverDownloadUrls: (urls: string[]) => void = downloadUrlsAsFiles
): Promise<boolean> {
  let successfullyDownloadedAllFiles = true;
  const children = await fetchFolderChildrenForDownload(
    client,
    { shareId },
    folderId,
    { first: PAGE_SIZE, afterOffset, mode: 'OFFSET' }
  );

  successfullyDownloadedAllFiles = await downloadAssets(
    client,
    children?.nodes ?? [],
    transcodeKey,
    downloadAllChildren,
    trackingFn,
    shareId,
    markSharedAssetsDownloaded,
    deliverDownloadUrls
  );

  if (children?.pageInfo.hasNextPage) {
    successfullyDownloadedAllFiles =
      (await downloadFolder(
        client,
        folderId,
        transcodeKey,
        downloadAllChildren,
        children.pageInfo.endOffset ?? undefined,
        trackingFn,
        shareId,
        markSharedAssetsDownloaded,
        deliverDownloadUrls
      )) && successfullyDownloadedAllFiles;
  }

  return successfullyDownloadedAllFiles;
}

/**
 * downloads the files for a set of non-folder assets and calls downloadFolder
 * for all folder asset
 */
async function downloadAssets(
  client: ApolloClient<any>,
  assets: DownloadableAsset[] | undefined,
  transcodeKey: TranscodeKeys,
  downloadAllChildren = DEFAULT_OPTS.downloadAllChildren,
  trackingFn: DownloadTrackingFn | undefined = undefined,
  shareId: string | undefined = undefined,
  markSharedAssetsDownloaded: MarkSharedAssetsDownloadedFn,
  deliverDownloadUrls: (urls: string[]) => void = downloadUrlsAsFiles
): Promise<boolean> {
  let successfullyDownloadedAllFiles = true;
  const [folderAssets, otherAssets] = partition(
    assets,
    (asset) => asset && asset.__typename === 'FolderAsset'
  );

  const urls = otherAssets.map(
    (asset) =>
      getTranscodeByKey(asset, transcodeKey ?? DEFAULT_ARGS.transcodeKey)
        ?.downloadUrl || null
  );

  if (urls.includes(null)) {
    successfullyDownloadedAllFiles = false;
  }

  if (trackingFn) trackingFn(otherAssets, transcodeKey);

  const assetIds = otherAssets.map((asset) => asset?.id ?? '');
  if (shareId && assetIds.length > 0) {
    markSharedAssetsDownloaded({
      variables: {
        input: {
          assetIds,
        },
      },
    });
  }
  const downloadUrls = urls.filter((u) => !!u) as string[];

  deliverDownloadUrls(downloadUrls);

  if (downloadAllChildren) {
    const successfullyDownloadedAllFilesArray = await Promise.all(
      folderAssets
        .filter((asset): asset is NonNullable<typeof asset> => asset !== null)
        .map((asset) =>
          downloadFolder(
            client,
            asset.id,
            transcodeKey,
            downloadAllChildren,
            0,
            trackingFn,
            shareId,
            markSharedAssetsDownloaded,
            deliverDownloadUrls
          )
        )
    );

    if (successfullyDownloadedAllFiles) {
      successfullyDownloadedAllFiles =
        !successfullyDownloadedAllFilesArray.includes(false);
    }
  }

  return successfullyDownloadedAllFiles;
}

/**
 * Calculates the file size of all the assets being downloaded in this batch.
 *
 * @param {*} assets The list of asset data to download
 * @param {*} transcodeKey The key for the requested trascode
 * @returns {number} Size of all the files in total (in bytes)
 */
function getDownloadFileSize(
  assets: AssetsForDownload,
  transcodeKey: TranscodeKeys
): number {
  // go through all the assets and add up the file size
  const totalFileSize =
    assets?.reduce((sum: number, asset) => {
      if (asset?.__typename !== 'FolderAsset') {
        const media = getTranscodeByKey(asset, transcodeKey);
        const filesize =
          media && 'filesizeInBytes' in media ? media.filesizeInBytes || 0 : 0;
        return sum + filesize;
      }
      return sum;
    }, 0) || 0;
  return totalFileSize;
}
